import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, BedDouble, Bath, MapPin, ArrowLeft, Check, Star, ChevronLeft, ChevronRight, MessageCircle,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { usePublicSettings } from "@/context/SettingsContext";
import { Calendar } from "@/components/ui/calendar";

const Field = ({ label, value, onChange, type = "text", required, testid, ...rest }) => (
  <label className="block">
    <span className="overline text-[10px] text-muted-foreground">{label}{required && " *"}</span>
    <input
      className="editorial-input mt-1 text-petrol"
      type={type}
      required={required}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      {...rest}
    />
  </label>
);

export default function ApartmentDetail() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const settings = usePublicSettings();
  const [apt, setApt] = useState(null);
  const [blocked, setBlocked] = useState([]);
  const [activePhoto, setActivePhoto] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [quote, setQuote] = useState(null);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", arrival_date: "", departure_date: "", guests: 1, message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sentWaLink, setSentWaLink] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/apartments/${id}`).then((r) => setApt(r.data)).catch(() => setApt(false));
    api.get(`/apartments/${id}/availability`).then((r) => setBlocked(r.data.blocked_dates || [])).catch(() => {});
    api.get(`/reviews`, { params: { apartment_id: id } }).then((r) => setReviews(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    const { arrival_date, departure_date } = form;
    if (arrival_date && departure_date && departure_date > arrival_date) {
      api.get(`/apartments/${id}/quote`, { params: { arrival: arrival_date, departure: departure_date } })
        .then((r) => setQuote(r.data)).catch(() => setQuote(null));
    } else {
      setQuote(null);
    }
  }, [id, form.arrival_date, form.departure_date]);

  if (apt === false) return <div className="pt-40 text-center text-muted-foreground">{t("common.error")}</div>;
  if (!apt) return <div className="pt-40 text-center text-muted-foreground">{t("common.loading")}</div>;

  const desc = lang === "it" ? apt.description : (apt.description_en || apt.description);
  const disabledDays = blocked.map((d) => new Date(d + "T00:00:00"));

  const buildWaLink = () => {
    const num = (settings?.whatsapp_number || "").replace(/[^\d]/g, "");
    if (!num) return "";
    const lines = [
      "Nuova richiesta di prenotazione",
      `Appartamento: ${apt.name}`,
      `Nome: ${form.full_name}`,
      `Email: ${form.email}`,
      `Telefono: ${form.phone}`,
      `Arrivo: ${form.arrival_date}`,
      `Partenza: ${form.departure_date}`,
      `Ospiti: ${form.guests}`,
    ];
    if (quote && quote.priced) lines.push(`Totale stimato: €${Math.round(quote.total)} (${quote.nights} notti)`);
    if (form.message) lines.push(`Messaggio: ${form.message}`);
    return `https://wa.me/${num}?text=${encodeURIComponent(lines.join("\n"))}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const waLink = buildWaLink();
    try {
      await api.post("/bookings", { ...form, apartment_id: id, guests: Number(form.guests) });
      toast.success(t("form.booking_success"));
      setSentWaLink(waLink || "");
      if (waLink) window.open(waLink, "_blank");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setForm({ full_name: "", email: "", phone: "", arrival_date: "", departure_date: "", guests: 1, message: "" });
    setSentWaLink(null);
  };

  const bind = (name) => ({ value: form[name], onChange: (v) => setForm({ ...form, [name]: v }), testid: `booking-${name}` });

  return (
    <div data-testid="apartment-detail-page" className="pt-28 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Link to="/apartments" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-petrol mb-8 link-underline" data-testid="back-link">
          <ArrowLeft size={16} strokeWidth={1.5} /> {t("detail.back")}
        </Link>

        {/* Header */}
        <div className="flex items-center gap-1.5 text-muted-foreground text-sm mb-2">
          <MapPin size={14} strokeWidth={1.5} /> {apt.location}
        </div>
        <h1 className="font-serif font-light text-4xl md:text-6xl text-petrol tracking-tight">{apt.name}</h1>
        {apt.rating_count > 0 && (
          <div className="flex items-center gap-2 mt-3" data-testid="detail-rating">
            <div className="flex gap-0.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} size={18} className={n <= Math.round(apt.rating_avg) ? "fill-gold text-gold" : "text-line"} />
              ))}
            </div>
            <span className="text-petrol font-medium">{apt.rating_avg}</span>
            <span className="text-muted-foreground text-sm">({apt.rating_count} recensioni)</span>
          </div>
        )}

        {/* Gallery */}
        <div className="mt-10 space-y-3" data-testid="apartment-gallery">
          <div className="relative aspect-[16/9] overflow-hidden rounded-sm bg-petrol/5 flex items-center justify-center">
            <img
              src={apt.photos?.[activePhoto]}
              alt={apt.name}
              className="w-full h-full object-contain"
              data-testid="gallery-main-image"
            />
            {apt.photos?.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => setActivePhoto((p) => (p - 1 + apt.photos.length) % apt.photos.length)}
                  data-testid="gallery-prev"
                  aria-label="Foto precedente"
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-cream/90 backdrop-blur flex items-center justify-center text-petrol shadow-md hover:bg-cream transition-colors"
                >
                  <ChevronLeft size={22} strokeWidth={1.5} />
                </button>
                <button
                  type="button"
                  onClick={() => setActivePhoto((p) => (p + 1) % apt.photos.length)}
                  data-testid="gallery-next"
                  aria-label="Foto successiva"
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-cream/90 backdrop-blur flex items-center justify-center text-petrol shadow-md hover:bg-cream transition-colors"
                >
                  <ChevronRight size={22} strokeWidth={1.5} />
                </button>
                <div className="absolute bottom-4 right-4 bg-petrol/80 text-cream text-xs px-2.5 py-1 rounded-full backdrop-blur">
                  {activePhoto + 1} / {apt.photos.length}
                </div>
              </>
            )}
          </div>
          {apt.photos?.length > 1 && (
            <div className="flex gap-3 overflow-x-auto pb-2" data-testid="gallery-thumbs">
              {apt.photos.map((p, i) => (
                <button
                  key={i}
                  onClick={() => setActivePhoto(i)}
                  className={`shrink-0 w-28 h-20 overflow-hidden rounded-sm border-2 transition-all ${activePhoto === i ? "border-gold" : "border-transparent hover:border-line"}`}
                  data-testid={`gallery-thumb-${i}`}
                >
                  <img src={p} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-14 mt-16">
          {/* Left content */}
          <div className="lg:col-span-2 space-y-14">
            <div className="flex flex-wrap gap-8 pb-10 border-b border-line">
              <span className="flex items-center gap-2 text-petrol"><Users size={18} strokeWidth={1.5} /> {apt.max_guests} {t("apartments.guests")}</span>
              <span className="flex items-center gap-2 text-petrol"><BedDouble size={18} strokeWidth={1.5} /> {apt.bedrooms} {t("apartments.bedrooms")}</span>
              <span className="flex items-center gap-2 text-petrol"><Bath size={18} strokeWidth={1.5} /> {apt.bathrooms} {t("apartments.bathrooms")}</span>
            </div>

            <section>
              <p className="overline text-gold mb-4">{t("detail.description")}</p>
              <p className="text-lg leading-relaxed text-petrol/80 font-light">{desc}</p>
            </section>

            {apt.amenities?.length > 0 && (
              <section>
                <p className="overline text-gold mb-5">{t("detail.amenities")}</p>
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                  {apt.amenities.map((a) => (
                    <div key={a} className="flex items-center gap-3 text-petrol/80 py-2 border-b border-line/60">
                      <Check size={16} className="text-gold shrink-0" /> {a}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {apt.house_rules?.length > 0 && (
              <section>
                <p className="overline text-gold mb-5">{t("detail.rules")}</p>
                <ul className="space-y-2">
                  {apt.house_rules.map((r) => (
                    <li key={r} className="text-petrol/80 flex items-center gap-3">
                      <span className="w-1 h-1 rounded-full bg-gold" /> {r}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {apt.map_address && (
              <section>
                <p className="overline text-gold mb-5">{t("detail.location")}</p>
                <div className="aspect-[16/9] rounded-sm overflow-hidden border border-line">
                  <iframe
                    title="map"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                    src={`https://www.google.com/maps?q=${encodeURIComponent(apt.map_address)}&output=embed`}
                  />
                </div>
              </section>
            )}

            <section>
              <p className="overline text-gold mb-5">{t("detail.availability")}</p>
              <div className="inline-block border border-line rounded-sm p-2 bg-card">
                <Calendar mode="single" disabled={disabledDays} numberOfMonths={1} data-testid="availability-calendar" />
              </div>
              {blocked.length > 0 && (
                <p className="text-xs text-muted-foreground mt-3">{t("detail.unavailable")}: {blocked.length}</p>
              )}
            </section>
          </div>

          {/* Booking widget */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-28 border border-line rounded-sm bg-card p-7">
              <div className="pb-5 border-b border-line" data-testid="price-box">
                {quote && quote.priced ? (
                  <>
                    <div className="flex items-baseline gap-2">
                      <span className="font-serif text-4xl text-petrol" data-testid="quote-total">€{Math.round(quote.total)}</span>
                      <span className="text-muted-foreground text-sm">{lang === "it" ? `totale · ${quote.nights} notti` : `total · ${quote.nights} nights`}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{lang === "it" ? "Media a notte" : "Avg / night"}: €{Math.round(quote.avg_per_night)}</p>
                  </>
                ) : quote && !quote.priced && quote.nights > 0 ? (
                  <p className="text-sm text-petrol" data-testid="quote-on-request">{lang === "it" ? "Prezzo su richiesta per le date selezionate" : "Price on request for these dates"}</p>
                ) : apt.from_price ? (
                  <div className="flex items-baseline gap-2">
                    <span className="text-muted-foreground text-sm">{t("apartments.from")}</span>
                    <span className="font-serif text-4xl text-petrol" data-testid="from-price">€{Math.round(apt.from_price)}</span>
                    <span className="text-muted-foreground text-sm">{t("apartments.per_night")}</span>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">{lang === "it" ? "Seleziona le date per vedere il prezzo" : "Select dates to see the price"}</p>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1 mb-5">{lang === "it" ? "Seleziona arrivo e partenza per il totale del soggiorno." : "Select arrival and departure for the stay total."}</p>

              {sentWaLink !== null ? (
                <div className="text-center py-4" data-testid="booking-confirmation">
                  <div className="w-12 h-12 rounded-full bg-green-100 text-green-700 flex items-center justify-center mx-auto mb-4"><Check size={24} /></div>
                  <p className="font-serif text-2xl text-petrol mb-1">{lang === "it" ? "Richiesta inviata!" : "Request sent!"}</p>
                  <p className="text-sm text-muted-foreground mb-5">{lang === "it" ? "Ti ricontatteremo a breve. Per una risposta più rapida, invia la richiesta anche su WhatsApp." : "We'll get back to you soon. For a faster reply, also send the request via WhatsApp."}</p>
                  {sentWaLink && (
                    <a href={sentWaLink} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-send-btn"
                      className="w-full flex items-center justify-center gap-2 bg-[#25D366] text-white py-3.5 rounded-sm hover:bg-[#1ebe5b] transition-colors mb-3">
                      <MessageCircle size={18} /> {lang === "it" ? "Invia su WhatsApp" : "Send on WhatsApp"}
                    </a>
                  )}
                  <button onClick={resetForm} data-testid="new-request-btn" className="text-sm text-petrol link-underline">{lang === "it" ? "Invia un'altra richiesta" : "Send another request"}</button>
                </div>
              ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="booking-form">                <Field label={t("form.name")} {...bind("full_name")} required />
                <Field label={t("form.email")} {...bind("email")} type="email" required />
                <Field label={t("form.phone")} {...bind("phone")} />
                <div className="grid grid-cols-2 gap-4">
                  <Field label={t("form.arrival")} {...bind("arrival_date")} type="date" required />
                  <Field label={t("form.departure")} {...bind("departure_date")} type="date" required />
                </div>
                <Field label={t("form.guests")} {...bind("guests")} type="number" min={1} max={apt.max_guests} required />
                <label className="block">
                  <span className="overline text-[10px] text-muted-foreground">{t("form.message")}</span>
                  <textarea
                    className="editorial-input mt-1 text-petrol resize-none"
                    rows={2}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    data-testid="booking-message"
                  />
                </label>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="booking-submit"
                  className="w-full bg-petrol text-white py-4 rounded-sm hover:bg-[#1A3A41] transition-colors disabled:opacity-60"
                >
                  {submitting ? t("form.sending") : t("form.send")}
                </button>
              </form>
              )}
            </div>
          </div>
        </div>

        {reviews.length > 0 && (
          <div className="mt-16" data-testid="detail-reviews">
            <p className="overline text-gold mb-5">Recensioni</p>
            <div className="grid md:grid-cols-2 gap-6">
              {reviews.map((rv) => (
                <div key={rv.id} className="border border-line bg-card p-6 md:p-8" data-testid={`detail-review-${rv.id}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex gap-1">
                      {[...Array(rv.rating)].map((_, i) => <Star key={i} size={15} className="fill-gold text-gold" />)}
                    </div>
                    {rv.source && rv.source !== "sito" && (
                      <span className="text-[11px] uppercase tracking-wide text-muted-foreground border border-line rounded-full px-2.5 py-1">
                        da {rv.source === "airbnb" ? "Airbnb" : rv.source === "booking" ? "Booking" : "Altro"}
                      </span>
                    )}
                  </div>
                  <p className="font-serif text-xl text-petrol leading-snug mb-4">“{rv.text}”</p>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-petrol">{rv.guest_name}</span>
                    <span className="text-muted-foreground">{[rv.country, rv.review_date].filter(Boolean).join(" · ")}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
